/**
 * ============================================================
 * KEJJA OS — src/hooks/usePantry.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pantryApi,
  type ItemCreatePayload, type ItemUpdatePayload, type ListFilter,
} from '@/lib/pantryApi';

const invalidate = (qc: ReturnType<typeof useQueryClient>, homeId: string) => {
  qc.invalidateQueries({ queryKey: ['pantry', homeId] });
};

export function usePantryPulse(homeId: string | undefined) {
  return useQuery({
    queryKey: ['pantry', homeId, 'pulse'],
    queryFn: () => pantryApi.pulse(homeId!),
    enabled: Boolean(homeId),
  });
}

export function usePantryItems(homeId: string | undefined, filter: ListFilter = {}) {
  return useQuery({
    queryKey: ['pantry', homeId, 'items', filter],
    queryFn: () => pantryApi.listItems(homeId!, filter),
    enabled: Boolean(homeId),
  });
}

export function useLowStock(homeId: string | undefined) {
  return useQuery({
    queryKey: ['pantry', homeId, 'low-stock'],
    queryFn: () => pantryApi.listLowStock(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useExpiringSoon(homeId: string | undefined, days = 7) {
  return useQuery({
    queryKey: ['pantry', homeId, 'expiring', days],
    queryFn: () => pantryApi.listExpiring(homeId!, days),
    enabled: Boolean(homeId),
  });
}

export function useCreatePantryItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ItemCreatePayload) => pantryApi.createItem(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdatePantryItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: ItemUpdatePayload }) =>
      pantryApi.updateItem(homeId!, itemId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useAdjustPantry(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, delta }: { itemId: string; delta: number }) =>
      pantryApi.adjustQuantity(homeId!, itemId, delta),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeletePantryItem(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => pantryApi.deleteItem(homeId!, itemId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}
